import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import type { UsageResponse } from '@/types';

interface QuotaConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  usage: UsageResponse | undefined;
  generationType: 'business-info' | 'content-strategy';
  websiteUrl?: string;
}

const QuotaConfirmationDialog: React.FC<QuotaConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  usage,
  generationType,
  websiteUrl,
}) => {
  // Use profile_scraping fields for business-info, content_strategy fields for content-strategy
  const monthlyUsed = generationType === 'business-info' 
    ? (usage?.profile_scraping_monthly_used ?? usage?.profile_scraping_used ?? 0)
    : (usage?.content_strategy_monthly_used ?? 0);
  const monthlyLimit = generationType === 'business-info'
    ? (usage?.profile_scraping_monthly_limit ?? usage?.profile_scraping_limit)
    : usage?.content_strategy_monthly_limit;
  const monthlyRemaining = generationType === 'business-info'
    ? (usage?.profile_scraping_monthly_remaining ?? usage?.profile_scraping_remaining ?? 0)
    : (usage?.content_strategy_monthly_remaining ?? 0);
  const isUnlimited = generationType === 'business-info'
    ? (usage?.profile_scraping_unlimited ?? false)
    : (usage?.content_strategy_unlimited ?? false);
  
  const dailyUsed = generationType === 'business-info'
    ? (usage?.profile_scraping_daily_used ?? 0)
    : (usage?.content_strategy_daily_used ?? 0);
  const dailyLimit = generationType === 'business-info'
    ? usage?.profile_scraping_daily_limit
    : usage?.content_strategy_daily_limit;
  // Use daily_remaining directly from API if available, otherwise calculate
  const dailyRemaining = generationType === 'business-info'
    ? (usage?.profile_scraping_daily_remaining !== undefined
        ? usage.profile_scraping_daily_remaining
        : (dailyLimit !== undefined ? Math.max(0, (dailyLimit || 0) - (dailyUsed || 0)) : undefined))
    : (usage?.content_strategy_daily_remaining !== undefined
        ? usage.content_strategy_daily_remaining
        : (dailyLimit !== undefined ? Math.max(0, (dailyLimit || 0) - (dailyUsed || 0)) : undefined));

  const title = generationType === 'business-info' 
    ? 'Confirm Business Information Generation'
    : 'Confirm Content Strategy Generation';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          '@media (max-width: 600px)': {
            margin: 1,
            maxWidth: 'calc(100% - 16px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 1.5 }}>
        {generationType === 'business-info' && websiteUrl && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Website URL:
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    fontWeight: 500,
                    wordBreak: 'break-all',
                  }}
                >
                  {websiteUrl}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.4 }}>
              You can only perform this action a limited number of times per day and month.
            </Typography>
          </Box>
        )}

        {generationType === 'content-strategy' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.4 }}>
              You can only perform this action a limited number of times per day and month.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Daily Quota */}
          {dailyLimit !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Daily:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {dailyUsed}/{dailyLimit}
                </Typography>
                {dailyRemaining !== undefined && dailyRemaining > 0 && (
                  <Chip 
                    label={`${dailyRemaining} left`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Monthly Quota */}
          {dailyLimit !== undefined && <Divider />}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Monthly:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isUnlimited ? (
                <Chip label="Unlimited" size="small" color="success" />
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {monthlyUsed}/{monthlyLimit ?? 'N/A'}
                  </Typography>
                  {monthlyRemaining > 0 && (
                    <Chip 
                      label={`${monthlyRemaining} left`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > button': {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: 120 },
          },
        }}
      >
        <Button onClick={onClose} variant="outlined" fullWidth={false}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" fullWidth={false}>
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotaConfirmationDialog;

